/**
 * MuiInputField – MUI TextField (variant="outlined") wrapper for all input types.
 * Preserves our InputField API; applies our theme via sx.
 */
import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { theme as v2Theme } from "../../theme/semanticTheme";

const baseSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: v2Theme.typography.fontFamily,
    fontSize: v2Theme.typography.inputValue.fontSize,
    fontWeight: v2Theme.typography.inputValue.fontWeight,
    lineHeight: v2Theme.typography.inputValue.lineHeight,
  },
  "& .MuiInputLabel-root": {
    fontFamily: v2Theme.typography.fontFamily,
    fontSize: v2Theme.typography.inputLabel.fontSize,
    fontWeight: v2Theme.typography.inputLabel.fontWeight,
    lineHeight: v2Theme.typography.inputLabel.lineHeight,
    backgroundColor: v2Theme.colors.background,
    padding: "0 4px",
    marginRight: 4,
    transformOrigin: "left top",
    top: "50%",
    transform: "translate(14px, -50%) scale(1)",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    backgroundColor: v2Theme.colors.background,
    padding: "0 4px",
    top: 0,
    transform: "translate(14px, -9px) scale(0.75)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: v2Theme.colors.border,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: v2Theme.colors.purple,
    borderWidth: "2px",
  },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: v2Theme.colors.error,
  },
};

export default function MuiInputField({
  name,
  type = "text",
  value,
  onChange,
  label,
  placeholder,
  disabled,
  required,
  readOnly,
  error,
  helperText,
  min,
  max,
  minLength,
  maxLength,
  pattern,
  options = [],
  multiple = false,
  rows,
  placeholderOnly = true,
  startAdornment,
  endAdornment,
  className,
  ...rest
}) {
  const isPassword = type === "password";
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(multiple ? (Array.isArray(v) ? v : [v]) : v);
  };

  const displayValue = value ?? (multiple ? [] : "");
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;

  const datePlaceholder =
    type === "date" || type === "datetime-local" ? (placeholder ?? "dd/mm/yyyy") : placeholder;

  const handleDateClick = (event) => {
    if ((type === "date" || type === "datetime-local") && event.target?.showPicker) {
      event.target.showPicker();
    }
  };

  const effectiveEndAdornment =
    endAdornment ??
    (isPassword ? (
      <InputAdornment position="end">
        <IconButton
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          edge="end"
          disabled={disabled}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ) : null);

  if (type === "select") {
    const hasValue = multiple
      ? Array.isArray(displayValue) && displayValue.length > 0
      : displayValue != null && displayValue !== "";
    return (
      <TextField
        name={name}
        select
        variant="outlined"
        fullWidth
        size="small"
        label={label}
        value={Array.isArray(displayValue) ? displayValue : displayValue ?? ""}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        error={Boolean(error)}
        helperText={error ?? helperText}
        InputProps={{
          startAdornment,
          endAdornment,
          readOnly,
        }}
        SelectProps={{
          multiple,
        }}
        InputLabelProps={{ shrink: hasValue || undefined }}
        sx={baseSx}
        className={className}
        {...rest}
      >
        {!multiple && (
          <MenuItem value="" disabled={placeholderOnly}>
            {placeholder || "Select..."}
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={String(opt.value)} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (type === "textarea") {
    return (
      <TextField
        name={name}
        variant="outlined"
        fullWidth
        size="small"
        multiline
        rows={rows ?? 3}
        label={label}
        value={displayValue ?? ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        error={Boolean(error)}
        helperText={error ?? helperText}
        InputProps={{
          startAdornment,
          endAdornment: endAdornment ?? undefined,
          readOnly,
        }}
        InputLabelProps={{ shrink: Boolean(displayValue) || undefined }}
        inputProps={{ minLength, maxLength }}
        sx={baseSx}
        className={className}
        {...rest}
      />
    );
  }

  return (
    <TextField
      name={name}
      type={inputType}
      variant="outlined"
      fullWidth
      size="small"
      label={label}
      value={displayValue ?? ""}
      onChange={handleChange}
      placeholder={datePlaceholder}
      disabled={disabled}
      required={required}
      error={Boolean(error)}
      helperText={error ?? helperText}
      InputProps={{
        startAdornment,
        endAdornment: effectiveEndAdornment,
        readOnly,
      }}
      inputProps={{ min, max, minLength, maxLength, pattern, onClick: handleDateClick }}
      InputLabelProps={{
        // For date/datetime-local we always shrink so label never overlaps the value/placeholder.
        shrink: type === "date" || type === "datetime-local" ? true : undefined,
      }}
      sx={baseSx}
      className={className}
      {...rest}
    />
  );
}
